import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FileCheck, User, LogOut, BarChart3, RefreshCw, AlertCircle,
  CheckCircle, XCircle, Clock, Upload, RotateCw, History, Trash2, Download, Loader2,
} from 'lucide-react';
import { apiService } from '../services/apiService';
import { trackEvent } from '../services/telemetry';
import { formatCurrency } from '../utils/formatting';

const PROCESSING_POLL_INTERVAL_MS = 10_000;
// History loads this many submitted POs at a time ("Show older" adds another page).
const HISTORY_PAGE = 200;
// Statuses with no A+ order behind them, so the CSR may remove them from the list.
const REMOVABLE_STATUSES = new Set(['processing', 'reviewing', 'failed']);
// processPO retries up to 5x (host.json maxDequeueCount) inside a 5-min functionTimeout,
// so anything still 'processing' after this long is stuck, not in flight.
const PROCESSING_POLL_WINDOW_MS = 30 * 60 * 1000;
// History checks unchecked orders submitted in the last 2 days in the background, at most 20 per visit.
const LAZY_CHECK_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;
const LAZY_CHECK_MAX = 20;

function formatTime(isoStr) {
  if (!isoStr) return '—';
  try {
    return new Date(isoStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return isoStr;
  }
}

// Local calendar-day key (not the raw ISO date) — CreatedAt is a UTC instant, so grouping
// must happen after converting to the CSR's own timezone, or a PO uploaded near midnight UTC
// could land in the wrong day's group for anyone west of it.
function localDateKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function formatGroupLabel(date) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (localDateKey(date) === localDateKey(today)) return 'Today';
  if (localDateKey(date) === localDateKey(yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

// When a PO belongs in the list: a submitted PO by when it was submitted (one uploaded days
// earlier and submitted today goes under Today, AI0IU 10/28), anything else by upload time.
// The backend orders the list the same way.
function listTime(s) {
  return s.Status === 'submitted' && s.SubmittedAt ? s.SubmittedAt : s.CreatedAt;
}

// Groups an already-most-recent-first list into consecutive same-local-day buckets,
// each retaining that order — no re-sort needed since the source query already is.
function groupByDate(sessions) {
  const groups = [];
  let current = null;
  for (const s of sessions) {
    const date = new Date(listTime(s));
    const key = localDateKey(date);
    if (!current || current.key !== key) {
      current = { key, label: formatGroupLabel(date), sessions: [] };
      groups.push(current);
    }
    current.sessions.push(s);
  }
  return groups;
}

// 'reviewing' is the internal status value (see backend pi_service.py) — shown to the
// CSR as "Ready for review". Kept as the wire value rather than renamed, since renaming
// it in the database would need a data migration for any row already sitting in it.
const STATUS_LABELS = {
  processing: 'Processing',
  reviewing: 'Ready for review',
  failed: 'Failed',
  submitted: 'Submitted',
  // Multi-warehouse split where some warehouses' orders reached A+ and others didn't.
  partial: 'Partially submitted',
};

// Whether an order transmitted to A+ intact: OP's item total vs A+'s, saved by the
// confirmation check (Tonya, 2026-10-28). Nothing to show until the order has been checked.
function TransmissionBadge({ t }) {
  if (!t) return null;
  const checked = t.checkedAt ? ` (checked ${new Date(t.checkedAt).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })})` : '';
  const title = `OP sent ${formatCurrency(t.opTotal)}, A+ has ${formatCurrency(t.aplusTotal)}`
    + (t.missingCount ? `, ${t.missingCount} line(s) not on the A+ order` : '') + `${checked}. Click to re-check.`;
  if (t.ok) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-400" title={title}>
        <CheckCircle className="w-3 h-3" /> Transmitted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs text-amber-400 font-medium" title={title}>
      <AlertCircle className="w-3 h-3" />
      {t.difference ? formatCurrency(t.difference) : `${t.missingCount} line(s) missing`}
    </span>
  );
}

// Both totals under the badge, so the CSR reads "OP $X · A+ $Y" without hovering.
function TransmissionAmounts({ t }) {
  if (!t) return null;
  return (
    <span className="block text-[11px] text-slate-500 font-mono">
      OP {formatCurrency(t.opTotal)} · A+ {formatCurrency(t.aplusTotal)}
    </span>
  );
}

// The order's A+ hold when it was last checked (CR credit, GM gross margin, SP slow pay, OH a
// line was dropped), saved with the transmission result (Tonya, 2026-10-29).
function HoldTag({ t }) {
  if (!t?.holdCode) return null;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 text-[11px] font-medium"
      title={`On hold in A+: ${t.holdCode}${t.holdDescription ? ` – ${t.holdDescription}` : ''}. Click the order to re-check.`}
    >
      Hold: {t.holdCode}
    </span>
  );
}

// Re-check one order against A+ (totals and hold) without downloading its confirmation.
function RecheckButton({ busy, onClick, orderNumber }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      disabled={busy}
      title={`Re-check ${orderNumber || 'this order'} in A+ (totals and hold)`}
      aria-label={`Re-check ${orderNumber || 'order'} in A+`}
      className="p-0.5 text-slate-500 hover:text-slate-200 disabled:opacity-50"
    >
      <RotateCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
    </button>
  );
}

const transmissionFor = (session, orderNumber) =>
  (session.Transmissions || []).find(t => t.orderNumber === orderNumber) || null;

// One A+ order per warehouse for a multi-warehouse split (s.Splits, from OrderSubmissionSplit).
// Each order that reached A+ has its own confirmation to download.
function SplitOrders({ splits, onDownload, downloadingKey, transmissions = [], onRecheck, isRechecking }) {
  return (
    <span className="flex flex-col items-end gap-0.5">
      {splits.map((sp) => (
        <span key={`${sp.aiOrderNumber}-${sp.warehouseId}`} className="flex flex-col items-end">
        <span className="flex items-center gap-1.5 text-xs" title={sp.error || undefined}>
          {sp.success
            ? <CheckCircle className="w-3 h-3 text-emerald-400" />
            : <XCircle className="w-3 h-3 text-red-400" />}
          <span className={`font-mono ${sp.success ? 'text-slate-300' : 'text-red-400'}`}>{sp.aiOrderNumber || '—'}</span>
          <span className="text-slate-500">WH {sp.warehouseId || '?'}</span>
          {sp.success && <HoldTag t={transmissions.find(t => t.orderNumber === sp.aiOrderNumber)} />}
          {sp.success && <TransmissionBadge t={transmissions.find(t => t.orderNumber === sp.aiOrderNumber)} />}
          {sp.success && sp.aiOrderNumber && onRecheck && (
            <RecheckButton orderNumber={sp.aiOrderNumber} busy={isRechecking?.(sp.aiOrderNumber)}
                           onClick={() => onRecheck(sp.aiOrderNumber)} />
          )}
          {sp.success && sp.aiOrderNumber && onDownload && (
            <button
              onClick={(e) => { e.stopPropagation(); onDownload(sp.aiOrderNumber); }}
              disabled={!!downloadingKey}
              title={`Download order confirmation ${sp.aiOrderNumber}`}
              className="p-0.5 text-amber-400 hover:text-amber-300 disabled:opacity-50"
            >
              {downloadingKey?.endsWith(`:${sp.aiOrderNumber}`)
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Download className="w-3 h-3" />}
            </button>
          )}
        </span>
        {sp.success && <TransmissionAmounts t={transmissions.find(t => t.orderNumber === sp.aiOrderNumber)} />}
        </span>
      ))}
    </span>
  );
}

function StatusBadge({ status }) {
  const label = STATUS_LABELS[status] || status || 'Unknown';
  const classes = {
    processing: 'bg-slate-500/10 text-slate-400',
    reviewing: 'bg-amber-500/10 text-amber-400',
    failed: 'bg-red-500/10 text-red-400',
    submitted: 'bg-emerald-500/10 text-emerald-400',
    partial: 'bg-orange-500/10 text-orange-400',
  }[status] || 'bg-slate-500/10 text-slate-400';

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${classes}`}>
      {status === 'processing' && <Clock className="w-3 h-3 animate-pulse" />}
      {status === 'reviewing' && <CheckCircle className="w-3 h-3" />}
      {status === 'failed' && <XCircle className="w-3 h-3" />}
      {status === 'submitted' && <CheckCircle className="w-3 h-3" />}
      {status === 'partial' && <AlertCircle className="w-3 h-3" />}
      {label}
    </span>
  );
}

export default function MyPOsDashboard({ user, onUploadNew, onResume }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryingId, setRetryingId] = useState(null);
  // "<correlationId>:<orderNumber>" of the confirmation being downloaded.
  const [downloadingKey, setDownloadingKey] = useState(null);
  // Why a confirmation couldn't be downloaded yet, or lines missing from its A+ order.
  const [notice, setNotice] = useState(null);
  // True tab counts from the backend; the lists themselves only hold what has been loaded.
  const [totals, setTotals] = useState(null);
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  // Both tabs read from the same fetchMyPOs() call — it already returns every status
  // (see pi_service.py::list_sessions_for_user's own docstring), so History is just a
  // client-side filter, not a separate endpoint.
  const [tab, setTab] = useState('active'); // 'active' | 'history'

  const activeSessions = useMemo(
    () => sessions.filter((s) => s.Status !== 'submitted'),
    [sessions],
  );
  const historySessions = useMemo(
    () => sessions.filter((s) => s.Status === 'submitted'),
    [sessions],
  );
  const visibleSessions = tab === 'active' ? activeSessions : historySessions;
  const groupedSessions = useMemo(() => groupByDate(visibleSessions), [visibleSessions]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.fetchMyPOs(historyLimit);
      setSessions(res.sessions || []);
      setTotals(res.totals || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [historyLimit]);

  useEffect(() => { loadData(); }, [loadData]);

  // Silent background refresh while something is still processing, so a PO flips to
  // "Ready for review" without the CSR hitting Refresh. Only counts recent rows: old test
  // sessions stuck at 'processing' (e.g. from before the queue worked) would otherwise
  // keep this polling forever.
  const hasFreshProcessing = useMemo(() => {
    const cutoff = Date.now() - PROCESSING_POLL_WINDOW_MS;
    return sessions.some((s) => s.Status === 'processing' && new Date(s.CreatedAt).getTime() > cutoff);
  }, [sessions]);

  useEffect(() => {
    if (!hasFreshProcessing) return undefined;
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const res = await apiService.fetchMyPOs(historyLimit);
        setSessions(res.sessions || []);
        setTotals(res.totals || null);
      } catch {
        // Transient failure — the next tick (or a manual Refresh) will catch up.
      }
    }, PROCESSING_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasFreshProcessing, historyLimit]);

  async function handleRetry(session) {
    setRetryingId(session.CorrelationId);
    try {
      await apiService.retryPO(session.CorrelationId);
      trackEvent('po_dashboard_retry_clicked', { correlationId: session.CorrelationId });
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setRetryingId(null);
    }
  }

  async function handleRemove(session) {
    setRemovingId(session.CorrelationId);
    try {
      await apiService.removePO(session.CorrelationId);
      trackEvent('po_dashboard_removed', { correlationId: session.CorrelationId, status: session.Status });
      setConfirmRemoveId(null);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingId(null);
    }
  }

  // Order confirmation PDF (Tonya, 2026-10-22), in place of the old "View changes" panel. It's
  // read from the order in A+, so it's only offered once the order is there.
  // Put a freshly checked transmission result on its row (the backend saves it too).
  const applyTransmission = useCallback((correlationId, orderNumber, t, missingCount, hold) => {
    if (!t) return;
    const entry = {
      ...t, orderNumber, missingCount, checkedAt: new Date().toISOString(),
      holdCode: hold?.code || null, holdDescription: hold?.description || null,
    };
    setSessions(prev => prev.map(s => (s.CorrelationId !== correlationId ? s : {
      ...s,
      Transmissions: [...(s.Transmissions || []).filter(x => x.orderNumber !== orderNumber), entry],
    })));
  }, []);

  async function downloadConfirmation(session, orderNumber) {
    setDownloadingKey(`${session.CorrelationId}:${orderNumber || ''}`);
    setNotice(null);
    try {
      const status = await apiService.checkOrderConfirmation(session.CorrelationId, orderNumber);
      if (!status.ready) {
        setNotice(status.conflict
          ? status.message
          : `Order ${orderNumber || ''} isn't in A+ yet. The confirmation will be ready once it is.`);
        return;
      }
      const t = status.transmission;
      applyTransmission(session.CorrelationId, orderNumber, t, status.missing?.length || 0, status.hold);
      if (status.missing?.length || (t && !t.ok) || status.hold) {
        // OP's total vs A+'s, item lines only (Tonya, 2026-10-28), plus any lines that didn't land.
        const totals = t ? `OP sent ${formatCurrency(t.opTotal)}, A+ has ${formatCurrency(t.aplusTotal)} (difference ${formatCurrency(t.difference)}). ` : '';
        const lines = status.missing?.length
          ? `Not on the A+ order: ${status.missing.map(m => `line ${m.lineNumber ?? '?'} (${m.itemNumber})`).join(', ')}. `
          : '';
        const hold = status.hold
          ? `On hold in A+: ${status.hold.code}${status.hold.description ? ` – ${status.hold.description}` : ''}. `
          : '';
        const fix = status.missing?.length || (t && !t.ok) ? 'Fix it in A+ before sending the confirmation.' : '';
        setNotice(`Order ${orderNumber}: ${hold}${t && !t.ok ? totals : ''}${lines}${fix}`.trim());
      }
      await apiService.downloadOrderConfirmation(session.CorrelationId, orderNumber);
      trackEvent('order_confirmation_downloaded', { correlationId: session.CorrelationId, orderNumber, from: 'my_pos' });
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloadingKey(null);
    }
  }

  // Recent submitted orders with no saved transmission result (the CSR left the Submitted
  // screen before the order reached A+) get checked once in the background, a few at a time,
  // so the History badges fill in. Older ones wait for a click. Each check reads A+.
  // One loop at a time: each result updates its row, which re-runs this effect, so a cleanup
  // that cancelled the loop would stop it after the first order.
  const lazyChecked = useRef(new Set());
  const lazyRunning = useRef(false);
  useEffect(() => {
    if (tab !== 'history' || lazyRunning.current || lazyChecked.current.size >= LAZY_CHECK_MAX) return;
    const cutoff = Date.now() - LAZY_CHECK_WINDOW_MS;
    const todo = [];
    for (const s of historySessions) {
      if (new Date(listTime(s)).getTime() < cutoff) continue;
      const orders = s.Splits?.length > 0
        ? s.Splits.filter(sp => sp.success && sp.aiOrderNumber).map(sp => sp.aiOrderNumber)
        : [s.AiOrderNumber].filter(Boolean);
      for (const orderNumber of orders) {
        const key = `${s.CorrelationId}:${orderNumber}`;
        if (!transmissionFor(s, orderNumber) && !lazyChecked.current.has(key)) {
          todo.push({ correlationId: s.CorrelationId, orderNumber, key });
        }
      }
    }
    if (todo.length === 0) return;
    lazyRunning.current = true;
    (async () => {
      for (const item of todo.slice(0, LAZY_CHECK_MAX - lazyChecked.current.size)) {
        lazyChecked.current.add(item.key);
        try {
          const status = await apiService.checkOrderConfirmation(item.correlationId, item.orderNumber);
          if (status.ready) {
            applyTransmission(item.correlationId, item.orderNumber, status.transmission, status.missing?.length || 0, status.hold);
          }
        } catch {
          // Leave the badge blank; a click re-checks.
        }
      }
      lazyRunning.current = false;
    })();
  }, [tab, historySessions, applyTransmission]);

  // Re-check without downloading: the ↻ icon on a row, or "Re-check A+" for every order
  // submitted in the last 2 days (e.g. after credit releases a batch of holds).
  const [recheckingKeys, setRecheckingKeys] = useState(() => new Set());
  const [recheckAllProgress, setRecheckAllProgress] = useState(null);
  const recheck = useCallback(async (correlationId, orderNumber, { quiet = false } = {}) => {
    const key = `${correlationId}:${orderNumber}`;
    setRecheckingKeys(prev => new Set(prev).add(key));
    try {
      const status = await apiService.checkOrderConfirmation(correlationId, orderNumber);
      if (status.ready) {
        applyTransmission(correlationId, orderNumber, status.transmission, status.missing?.length || 0, status.hold);
        trackEvent('po_dashboard_rechecked', { correlationId, orderNumber, quiet });
      } else if (!quiet) {
        setNotice(status.conflict
          ? status.message
          : `Order ${orderNumber} isn't in A+ yet. Try again in a minute or two.`);
      }
    } catch (err) {
      if (!quiet) setError(err.message);
    } finally {
      setRecheckingKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  }, [applyTransmission]);

  async function recheckAllRecent() {
    const cutoff = Date.now() - LAZY_CHECK_WINDOW_MS;
    const todo = [];
    for (const s of historySessions) {
      if (new Date(listTime(s)).getTime() < cutoff) continue;
      const orders = s.Splits?.length > 0
        ? s.Splits.filter(sp => sp.success && sp.aiOrderNumber).map(sp => sp.aiOrderNumber)
        : [s.AiOrderNumber].filter(Boolean);
      orders.forEach(orderNumber => todo.push([s.CorrelationId, orderNumber]));
    }
    setNotice(null);
    setRecheckAllProgress({ done: 0, total: todo.length });
    for (let i = 0; i < todo.length; i++) {
      await recheck(todo[i][0], todo[i][1], { quiet: true });
      setRecheckAllProgress({ done: i + 1, total: todo.length });
    }
    setRecheckAllProgress(null);
  }

  function handleRowClick(session) {
    if (session.Status === 'reviewing') {
      trackEvent('po_dashboard_resume_clicked', { correlationId: session.CorrelationId });
      onResume(session.CorrelationId, session.AiOrderNumber);
    }
    // Submitted POs download their confirmation from the Confirmation button only, and
    // re-check from the ↻ icon, so a stray click on the row doesn't start a download.
  }

  function handleTabChange(nextTab) {
    setTab(nextTab);
    trackEvent('po_dashboard_tab_changed', { tab: nextTab });
  }

  useEffect(() => {
    trackEvent('po_dashboard_viewed');
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Auth Header — same shape as UploadStage's */}
      <header className="px-6 py-3 flex items-center justify-end">
        {user && (
          <div className="flex items-center gap-4">
            {user.userRoles?.includes('po_processor_admins') && (
              <a
                href="/admin"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Admin
              </a>
            )}
            <div className="flex items-center gap-2 text-slate-300">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-slate-400" />
              </div>
              <span className="text-sm">{user.userDetails}</span>
            </div>
            <a
              href="/.auth/logout?post_logout_redirect_uri=/"
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </a>
          </div>
        )}
      </header>

      <div className="flex-1 px-6 pb-10 max-w-6xl w-full mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <FileCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">My POs</h1>
              <p className="text-slate-400 text-sm">Purchase orders you've submitted for processing</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={onUploadNew}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-lg transition-colors"
            >
              <Upload className="w-4 h-4" />
              Upload a new PO
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {notice && (
          <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <p className="text-amber-300 text-sm flex-1">{notice}</p>
            <button onClick={() => setNotice(null)} className="text-amber-400/70 hover:text-amber-300 text-xs">Dismiss</button>
          </div>
        )}

        <div className="flex items-center gap-1 mb-4 border-b border-slate-700">
          <button
            onClick={() => handleTabChange('active')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'active'
                ? 'text-amber-400 border-amber-400'
                : 'text-slate-400 border-transparent hover:text-slate-200'
            }`}
          >
            Active <span className="text-xs opacity-70">({totals?.active ?? activeSessions.length})</span>
          </button>
          <button
            onClick={() => handleTabChange('history')}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === 'history'
                ? 'text-amber-400 border-amber-400'
                : 'text-slate-400 border-transparent hover:text-slate-200'
            }`}
          >
            <History className="w-3.5 h-3.5" />
            History <span className="text-xs opacity-70">({totals?.history ?? historySessions.length})</span>
          </button>
          {tab === 'history' && (
            <button
              onClick={recheckAllRecent}
              disabled={!!recheckAllProgress}
              title="Re-check every order submitted in the last 2 days against A+ (totals and holds)"
              className="ml-auto mb-1 flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-60"
            >
              <RotateCw className={`w-3.5 h-3.5 ${recheckAllProgress ? 'animate-spin' : ''}`} />
              {recheckAllProgress
                ? `Re-checking ${recheckAllProgress.done}/${recheckAllProgress.total}…`
                : 'Re-check A+ (last 2 days)'}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-400 border-t-transparent" />
          </div>
        ) : visibleSessions.length === 0 ? (
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl py-16 text-center">
            <FileCheck className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            {tab === 'active' ? (
              <>
                <p className="text-slate-400">You haven't submitted any POs yet.</p>
                <button
                  onClick={onUploadNew}
                  className="mt-4 px-4 py-2 text-sm font-semibold bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-lg transition-colors"
                >
                  Upload your first PO
                </button>
              </>
            ) : (
              <p className="text-slate-400">Nothing here yet — submitted orders show up once they've made it to A+.</p>
            )}
          </div>
        ) : (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700 bg-slate-800/80">
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">File</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Items</th>
                    <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {tab === 'history' ? 'A+ Order' : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groupedSessions.map((group) => (
                    <React.Fragment key={group.key}>
                      <tr className="bg-slate-800/60">
                        <td colSpan={7} className="py-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wide">
                          {group.label}
                        </td>
                      </tr>
                      {group.sessions.map((s) => {
                        const isResumable = s.Status === 'reviewing';
                        const isFailed = s.Status === 'failed';
                        return (
                          <tr
                            key={s.CorrelationId}
                            onClick={() => handleRowClick(s)}
                            className={`border-b border-slate-700/50 ${isResumable ? 'hover:bg-slate-700/30 cursor-pointer' : ''}`}
                          >
                            <td className="py-2.5 px-3 text-slate-400 text-xs whitespace-nowrap">{formatTime(listTime(s))}</td>
                            <td className="py-2.5 px-3 text-white font-medium font-mono text-xs">{s.AiOrderNumber || '—'}</td>
                            <td className="py-2.5 px-3 text-slate-300 text-xs max-w-[160px] truncate" title={s.FileName}>{s.FileName || '—'}</td>
                            <td className="py-2.5 px-3 text-slate-300 text-xs max-w-[160px] truncate" title={s.ExtCustomerName}>
                              {s.ExtCustomerName || s.ValCustomerName || '—'}
                            </td>
                            <td className="py-2.5 px-3">
                              <StatusBadge status={s.Status} />
                              {isFailed && s.FailureReason && (
                                <p className="text-red-400/70 text-xs mt-1 max-w-[220px]">{s.FailureReason}</p>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-slate-300">{s.ExtItemCount ?? '—'}</td>
                            <td className="py-2.5 px-3 text-right">
                              {tab === 'history' ? (
                                <>
                                <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
                                  {s.Splits?.length > 0 ? (
                                    <SplitOrders
                                      splits={s.Splits}
                                      transmissions={s.Transmissions}
                                      onRecheck={(orderNumber) => recheck(s.CorrelationId, orderNumber)}
                                      isRechecking={(orderNumber) => recheckingKeys.has(`${s.CorrelationId}:${orderNumber}`)}
                                      onDownload={(orderNumber) => downloadConfirmation(s, orderNumber)}
                                      downloadingKey={downloadingKey?.startsWith(`${s.CorrelationId}:`) ? downloadingKey : null}
                                    />
                                  ) : (
                                    <>
                                      <HoldTag t={transmissionFor(s, s.AiOrderNumber)} />
                                      <TransmissionBadge t={transmissionFor(s, s.AiOrderNumber)} />
                                      <RecheckButton orderNumber={s.AiOrderNumber}
                                                     busy={recheckingKeys.has(`${s.CorrelationId}:${s.AiOrderNumber}`)}
                                                     onClick={() => recheck(s.CorrelationId, s.AiOrderNumber)} />
                                      <span className="text-slate-400 font-mono text-xs">{s.ErpOrderNumber || '—'}</span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); downloadConfirmation(s, s.AiOrderNumber); }}
                                        disabled={!!downloadingKey}
                                        className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs font-medium whitespace-nowrap disabled:opacity-50"
                                        title={`Download order confirmation ${s.AiOrderNumber}`}
                                      >
                                        {downloadingKey?.startsWith(`${s.CorrelationId}:`)
                                          ? <Loader2 className="w-3 h-3 animate-spin" />
                                          : <Download className="w-3 h-3" />}
                                        Confirmation
                                      </button>
                                    </>
                                  )}
                                </span>
                                {!(s.Splits?.length > 0) && <TransmissionAmounts t={transmissionFor(s, s.AiOrderNumber)} />}
                                </>
                              ) : (
                                <>
                                  {s.Status === 'partial' && s.Splits?.length > 0 && (
                                    <SplitOrders
                                      splits={s.Splits}
                                      onDownload={(orderNumber) => downloadConfirmation(s, orderNumber)}
                                      downloadingKey={downloadingKey?.startsWith(`${s.CorrelationId}:`) ? downloadingKey : null}
                                    />
                                  )}
                                  {isResumable && (
                                    <span className="text-amber-400 text-xs font-medium">Review &rarr;</span>
                                  )}
                                  {isFailed && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleRetry(s); }}
                                      disabled={retryingId === s.CorrelationId}
                                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                      <RotateCw className={`w-3 h-3 ${retryingId === s.CorrelationId ? 'animate-spin' : ''}`} />
                                      Retry
                                    </button>
                                  )}
                                  {REMOVABLE_STATUSES.has(s.Status) && (
                                    confirmRemoveId === s.CorrelationId ? (
                                      <span className="inline-flex items-center gap-1.5 ml-2 text-xs" onClick={(e) => e.stopPropagation()}>
                                        <span className="text-slate-300">Remove {s.AiOrderNumber || 'this PO'}?</span>
                                        <button
                                          onClick={() => handleRemove(s)}
                                          disabled={removingId === s.CorrelationId}
                                          className="px-2 py-1 font-medium text-white bg-red-600 hover:bg-red-500 rounded-md transition-colors disabled:opacity-50"
                                        >
                                          {removingId === s.CorrelationId ? 'Removing…' : 'Yes'}
                                        </button>
                                        <button
                                          onClick={() => setConfirmRemoveId(null)}
                                          className="px-2 py-1 text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-md transition-colors"
                                        >
                                          No
                                        </button>
                                      </span>
                                    ) : (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setConfirmRemoveId(s.CorrelationId); }}
                                        title="Remove from My POs (not submitted, nothing is sent to A+)"
                                        aria-label={`Remove ${s.AiOrderNumber || 'PO'}`}
                                        className="ml-2 inline-flex items-center p-1 text-slate-500 hover:text-red-400 rounded transition-colors align-middle"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {tab === 'history' && totals && historySessions.length < totals.history && (
              <div className="border-t border-slate-700 px-3 py-3 text-center">
                <button
                  onClick={() => setHistoryLimit((n) => n + HISTORY_PAGE)}
                  className="px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  Show older ({historySessions.length} of {totals.history} shown)
                </button>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
